import classNames from 'classnames'
import { observer } from 'mobx-react-lite'
import { useEffect, useMemo, useRef } from 'react'
import { AutomatedAvatar, Icon, Typography } from 'shared/ui'
import { getAriaLabel } from 'shared/lib'
import { type Attachment } from 'entities/Attachment/model/Attachment'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { CallContent } from 'widgets/MessageCard/ui/CallContent/CallContent'
import { ImagesContent } from 'widgets/MessageCard/ui/ImagesContent/ImagesContent'
import { VideoContent } from 'widgets/MessageCard/ui/VideoContent/VideoContent'
import { TextContent } from 'widgets/MessageCard/ui/TextContent/TextContent'
import { OtherAttachmentContentContent } from 'widgets/MessageCard/ui/OtherAttachmentContentContent/OtherAttachmentContentContent'
import { AttachmentContent } from 'widgets/MessageCard/ui/AttachmentContent'
import { AvatarContent } from 'widgets/AvatarContent'
import { useMessageCardContext } from 'widgets/MessageCard/context/messageCardContext'
import { TextMeetContent } from 'widgets/MessageCard/ui/TextMeetContent/TextMeetContent'
import styles from './styles.module.scss'

export const MessageCardContent = observer(() => {
  const {
    message,
    selectedSearch,
    callContentStore,
    setScrollBottomTrigger,
    isLast,
    hideTeam,
    conversationMessagesStore,
  } = useMessageCardContext()
  const { attachmentStore } = useEntitiesContext()

  const isHideAvatars = conversationMessagesStore?.isHideAvatars
  const refContent = useRef<HTMLDivElement>(null)

  const {
    isOutbound,
    type,
    isHideAvatar,
    isAirCall,
    links,
    isAutomated,
    isConversationsHistory,
    isTransferred,
    teamName,
  } = message

  const attachments: Attachment[] = []
  links.forEach((link) => {
    const attachment = attachmentStore.getAttachmentByLink(link)
    if (attachment) {
      attachments.push(attachment)
    }
  })

  useEffect(() => {
    if (refContent.current) {
      const dates: NodeListOf<HTMLDivElement> = refContent.current.querySelectorAll(
        '[data-name="message-time"]'
      ) // message-time
      dates.forEach((date, index) => {
        if (index !== dates.length - 1) {
          date.setAttribute('style', 'display: none;')
        }
      })
      const messages: NodeListOf<HTMLDivElement> = refContent.current.querySelectorAll(
        '[data-name="message-sender"]'
      ) // message-sender
      messages.forEach((element, index) => {
        if (index !== 0) {
          element.setAttribute('style', 'display: none;')
        }
      })
    }
  }, [attachments, message])

  const content = useMemo(() => {
    if (message.source === 'salesmessage_chatbot') {
      return <TextMeetContent message={message} />
    }

    if (type === 'call' || isAirCall)
      return (
        <CallContent
          message={message}
          isLast={isLast}
          setScrollBottomTrigger={() => {
            setScrollBottomTrigger?.()
          }}
          callContentStore={callContentStore}
          conversationMessagesStore={conversationMessagesStore ?? undefined}
        />
      )

    return (
      <>
        <ImagesContent message={message} onLoad={conversationMessagesStore?.onLoadMediaContent} />
        <VideoContent message={message} onLoad={conversationMessagesStore?.onLoadMediaContent} />
        <TextContent message={message} selectedSearch={selectedSearch || ''} />
        <OtherAttachmentContentContent
          message={message}
          onLoad={conversationMessagesStore?.onLoadMediaContent}
        />
        <AttachmentContent
          message={message}
          attachments={attachments}
          onLoad={conversationMessagesStore?.onLoadMediaContent}
        />
      </>
    )
  }, [message, attachments])

  const avatar = useMemo(() => {
    if (isAutomated) {
      return <AutomatedAvatar withTooltip automatedInfo={message.automatedInfo} />
    }

    return <AvatarContent avatarInfo={message.avatarInfo} avatarProps={message.avatarProps} />
  }, [isAutomated, message.automatedInfo, message.avatarInfo, message.avatarProps])

  const avatarContent = useMemo(() => {
    if (hideTeam) {
      return null
    }

    if (isConversationsHistory || isTransferred) {
      return (
        <div className={styles.avatarWrap}>
          {!isOutbound && <>{avatar}</>}
          <div className={styles.icon}>
            <Icon icon={'inboxOpen'} />
          </div>
          <Typography variant={'body-sm-regular'} ariaLabel={'name'} className={styles.name}>
            {teamName}
          </Typography>
          {isOutbound && <>{avatar}</>}
        </div>
      )
    }

    if (isHideAvatar) {
      return <div style={{ width: 24 }} />
    }

    return <>{avatar}</>
  }, [isHideAvatar, isConversationsHistory, hideTeam, isTransferred])

  if (isConversationsHistory || isTransferred) {
    return (
      <div
        id={String(message.client_id)}
        key={message.client_id}
        ref={refContent}
        aria-label={getAriaLabel('MessageCard')}
      >
        <div
          className={classNames(styles.wrap, styles.unifiedConversationHistory, {
            [styles.outbox]: isOutbound,
          })}
        >
          {avatarContent}
          <div className={styles.contentWrap}>{content}</div>
        </div>
      </div>
    )
  }

  return (
    <div
      id={String(message.client_id)}
      key={message.client_id}
      ref={refContent}
      aria-label={getAriaLabel('MessageCard')}
    >
      <div
        className={classNames(styles.wrap, {
          [styles.isHideAvatars]: isHideAvatars,
          [styles.outbox]: isOutbound,
        })}
      >
        {!isHideAvatars && !isOutbound && avatarContent}
        <div className={styles.contentWrap}>{content}</div>
        {!isHideAvatars && isOutbound && avatarContent}
      </div>
    </div>
  )
})
